import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppService } from '../../../services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import Swal from "sweetalert2";
import { CategoryDetails } from '../../../models/category-details.model';

@Component({
  selector: 'app-admin-categories',
  templateUrl: './admin-categories.component.html',
  styleUrls: ['./admin-categories.component.scss']
})
export class AdminCategoriesComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();
  categories: CategoryDetails[] = [];

  isModalOpen = false;
  isEditMode = false;
  currentCategoryId: number | null = null;
  isLoading = false;

  categoryForm = new UntypedFormGroup({
    categoryName: new UntypedFormControl("", [Validators.required, Validators.nullValidator]),
    categoryImage: new UntypedFormControl("", [Validators.nullValidator]),
    imageData: new UntypedFormControl("", [Validators.nullValidator])
  });

  categoryFormErrors = {
    categoryName: ''
  };

  constructor(
    private appService: AppService,
    private toaster: ToastrService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.loadCategories();
  }

  ngAfterViewInit(): void {
    if (window.history.state && window.history.state.openAddCategoryModal) {
      setTimeout(() => {
        this.openAddModal();
      }, 300);
    }
  }

  loadCategories(): void {
    this.isLoading = true;
    this.appService.getAllCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.categories = data;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading categories', err);
          this.toaster.error('Failed to load categories');
          this.isLoading = false;
        }
      });
  }

  openAddModal(): void {
    this.isEditMode = false;
    this.currentCategoryId = null;
    this.categoryForm.reset();
    this.isModalOpen = true;
  }

  openEditModal(category: CategoryDetails): void {
    this.isEditMode = true;
    this.currentCategoryId = category.categoryId;
    this.categoryForm.patchValue({
      categoryName: category.categoryName,
      categoryImage: category.categoryImage
    });
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  onSubmit(): void {
    this.formValidation();
  }

  formValidation(): void {
    let hasError = false;
    Object.keys(this.categoryFormErrors).forEach(key => {
      // @ts-ignore
      this.categoryFormErrors[key] = '';
    });

    if (this.categoryForm.get('categoryName')?.invalid) {
      this.categoryFormErrors.categoryName = '*Category name is required';
      hasError = true;
    }

    if (!hasError) {
      this.saveCategory();
    }
  }

  saveCategory(): void {
    this.spinner.show();
    const payload = this.categoryForm.value;

    if (this.isEditMode && this.currentCategoryId) {
      payload.categoryId = this.currentCategoryId;
      this.appService.updateCategory(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.successPopup('Category Updated Successfully');
          },
          error: (err) => {
            console.error('Update category error:', err);
            this.spinner.hide();
            this.errorPopup();
          }
        });

    } else {
      payload.categoryId = 0; // Default ID for insert

      // The API endpoint is bulk-insert, so we wrap the single payload in an array
      this.appService.addCategories([payload])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.successPopup('Category Added Successfully');
          },
          error: (err) => {
            console.error('Save category error:', err);
            this.spinner.hide();
            this.errorPopup();
          }
        });
    }
  }

  toggleCategoryAvailability(category: CategoryDetails, event: any): void {
    const isChecked = event.target.checked;
    this.spinner.show();

    // Create an updated category payload with the modified active status
    const updatePayload = {
      ...category,
      active: isChecked
    };

    this.appService.updateCategory(updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.toaster.success(`Category ${isChecked ? 'enabled' : 'disabled'} successfully`);
          // Update local state to match successful server call
          category.active = isChecked;
        },
        error: (err) => {
          console.error('Update category status error:', err);
          this.spinner.hide();
          this.errorPopup();
          // Revert toggle state in UI
          event.target.checked = !isChecked;
        }
      });
  }

  successPopup(msg: string) {
    this.closeModal();
    this.loadCategories();
    Swal.fire({
      icon: "success",
      title: msg
    });
  }

  errorPopup() {
    Swal.fire({
      icon: "error",
      title: "Something went wrong, try again later"
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
